import { useState, useCallback } from 'react';
import { ReferralData, DeduplicationMatch } from '../types';
import { sendToCC } from 'src/services/leadManagementApi';
import { buildMergeRecordPayloadBuilder } from '../utils/buildSendToCCPayload';

export const useMergeModal = () => {
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [selectedMergeRow, setSelectedMergeRow] = useState<ReferralData | null>(null);
  const [selectedMergeTarget, setSelectedMergeTarget] = useState<DeduplicationMatch | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'confirm' | 'result'>('result');
  const [resultMessage, setResultMessage] = useState('');
  const [resultType, setResultType] = useState<'success' | 'error'>('success');
  const [isSending, setIsSending] = useState(false);

  const handleOpenMergeModal = useCallback(
    (match: DeduplicationMatch | undefined, row: ReferralData) => {
      if (!row) return;
      if (!match) return;

      setSelectedMergeRow(row);
      setSelectedMergeTarget(match);
      setMergeModalOpen(true);
    },
    []
  );

  const handleCloseMergeModal = useCallback(() => {
    setMergeModalOpen(false);
  }, []);

  const handleMerge = useCallback(
    async (
      selectedFields: string[],
      setSelectedFields: (fields: string[]) => void,
      confirmationModalMessages: any,
      onClose: () => void
    ) => {
      if (!selectedMergeRow || !selectedMergeTarget) return;

      setIsSending(true);

      try {
        const payload = buildMergeRecordPayloadBuilder(
          selectedMergeRow,
          String(selectedMergeTarget.id),
          selectedFields,
          'Auto-Merge'
        );

        const response = await sendToCC(payload);

        if (response.success) {
          setIsSending(false);

          const resources = response.resources || {};
          const resourceTypes = [
            { key: 'conditions', label: 'Conditions' },
            { key: 'procedures', label: 'Procedures' },
            { key: 'medications', label: 'Medications' },
            { key: 'immunizations', label: 'Immunizations' },
            { key: 'allergies', label: 'Allergies' },
            { key: 'familyMemberHistory', label: 'Family Member History' },
            { key: 'devices', label: 'Devices' },
          ];

          const failedResources: string[] = [];
          const errorMessages: string[] = [];
          let hasSkippedResources = false;

          resourceTypes.forEach(({ key, label }) => {
            const resourceData = resources[key];
            if (resourceData && resourceData !== null) {
              if (resourceData.ccError) {
                failedResources.push(label);
                errorMessages.push(resourceData.ccError);
              }
              if (resourceData.hasSkippedResources === true) {
                hasSkippedResources = true;
              }
            }
          });

          const hasResourceError = failedResources.length > 0;

          // Build skipped resources message
          const skippedResourceMessage = hasSkippedResources
            ? 'Some Resources were skipped, check the full User profile in processed tab for more details.'
            : '';

          if (hasResourceError) {
            setResultType('success');
            const partialSuccessMessage =
              confirmationModalMessages?.ConfirmationModalPartialSuccessMessage?.value || '-';

            const failedResourcesList = failedResources.join(', ');
            const combinedErrors = errorMessages.join(', ');

            let formattedMessage = partialSuccessMessage
              .replace('[resources]', failedResourcesList)
              .replace('[ccError]', combinedErrors)
              .replace('[skippedResources]', skippedResourceMessage);

            // Remove empty lines caused by empty placeholder replacement
            formattedMessage = formattedMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(formattedMessage);
          } else {
            setResultType('success');
            let successMessage =
              confirmationModalMessages?.ConfirmationModalSuccessMessage?.value ||
              'Record merged and sent to CC successfully';
            successMessage = successMessage
              .replace('[ccId]', response?.ccId || selectedMergeTarget.ccPatientReferenceId)
              .replace('[skippedResources]', skippedResourceMessage);

            // Remove empty lines caused by empty placeholder replacement
            successMessage = successMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(successMessage);
          }
          setDialogMode('result');
          setShowDialog(true);
          setSelectedFields([]);
          onClose();
        } else {
          setIsSending(false);
          setResultType('error');
          setResultMessage(
            confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
              response.message ||
              'Failed to merge and send record to CC'
          );
          setDialogMode('result');
          setShowDialog(true);
          setSelectedFields([]);
          onClose();
        }
      } catch (error: any) {
        setIsSending(false);
        setResultType('error');
        setResultMessage(
          confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
            error?.response?.data?.message ||
            error.message ||
            'Unknown error occurred'
        );
        setDialogMode('result');
        setShowDialog(true);
        setSelectedFields([]);
        onClose();
      }
    },
    [selectedMergeRow, selectedMergeTarget, handleCloseMergeModal]
  );

  return {
    mergeModalOpen,
    selectedMergeRow,
    selectedMergeTarget,
    handleOpenMergeModal,
    handleCloseMergeModal,
    handleMerge,
    showDialog,
    dialogMode,
    resultMessage,
    resultType,
    isSending,
    setShowDialog,
  };
};
